import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { logOut, hasRole } from '../services/auth';
import { Menu, X, LogOut, User, Home, MapPin, Calendar, Shield, Settings, ChevronDown, Package, Plus, MessageSquare } from 'lucide-react';

const AppHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);

  const handleLogout = async () => {
    try {
      await logOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  // Client navigation
  const clientNavItems = [
    { path: '/client/home', label: 'Home', icon: Home },
    { path: '/client/trips', label: 'Browse Trips', icon: MapPin },
    { path: '/client/bookings', label: 'My Trips', icon: Package },
  ];

  // Host navigation
  const hostNavItems = [
    { path: '/host/dashboard', label: 'Dashboard', icon: Home },
    { path: '/host/trips', label: 'My Listings', icon: MapPin },
    { path: '/host/bookings', label: 'Bookings', icon: Calendar },
    { path: '/host/messages', label: 'Messages', icon: MessageSquare },
  ];

  // Admin navigation
  const adminNavItems = [
    { path: '/admin', label: 'Dashboard', icon: Shield },
    { path: '/admin/hosts', label: 'Host Approvals', icon: User },
    { path: '/admin/trips', label: 'Trip Approvals', icon: MapPin },
  ];

  const getNavItems = () => {
    if (hasRole(user, 'client')) return clientNavItems;
    if (hasRole(user, 'host')) return hostNavItems;
    if (hasRole(user, 'admin')) return adminNavItems;
    return [];
  };

  const navItems = getNavItems();

  // Get profile and settings routes based on role
  const getProfileRoute = () => {
    if (hasRole(user, 'host')) return '/host/profile';
    if (hasRole(user, 'client')) return '/client/profile';
    return '/profile';
  };

  const getSettingsRoute = () => {
    return '/settings';
  };

  // Get role-specific menu items for dropdown
  // Excludes items that are already in the main navigation
  const getRoleMenuItems = () => {
    if (hasRole(user, 'host')) {
      // Filter out items that are already in hostNavItems (My Listings, Bookings, Messages)
      return [
        { path: '/host/profile', label: 'Profile', icon: User },
        { path: '/host/trips/create', label: 'Create Listing', icon: Plus },
        { path: '/settings', label: 'Settings', icon: Settings },
      ];
    }
    if (hasRole(user, 'client')) {
      // Filter out items that are already in clientNavItems (Browse Trips, My Trips)
      return [
        { path: '/client/profile', label: 'Profile', icon: User },
        { path: '/settings', label: 'Settings', icon: Settings },
      ];
    }
    return [
      { path: getProfileRoute(), label: 'Profile', icon: User },
      { path: '/settings', label: 'Settings', icon: Settings },
    ];
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const roleMenuItems = getRoleMenuItems();
  const isHost = hasRole(user, 'host');
  const isClient = hasRole(user, 'client');

  return (
    <header className={`border-b sticky top-0 backdrop-blur-md z-40 ${
      isHost 
        ? 'border-emerald-200 bg-emerald-50/30' 
        : isClient 
        ? 'border-emerald-100 bg-white/80'
        : 'border-emerald-100 bg-white/80'
    }`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 md:px-8">
          {/* Logo */}
          <Link to={isClient ? '/client/home' : isHost ? '/host/dashboard' : '/admin'} className="flex items-center space-x-2 sm:space-x-3">
            <img
              src={`${process.env.PUBLIC_URL || ''}/logo192.png`}
              alt="Spirara"
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl shadow-lg"
            />
            <span className="text-xl sm:text-2xl font-light text-gray-900">
              Spirara<span className="text-emerald-700">.</span>
              {isHost && <span className="text-xs text-emerald-600 ml-2 font-normal">Host</span>}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm lg:text-base font-medium transition-colors duration-200 flex items-center gap-2 ${
                    isActive(item.path)
                      ? 'text-emerald-700 bg-emerald-50'
                      : 'text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User Menu */}
          <div className="hidden md:flex items-center gap-4">
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50/50 hover:bg-emerald-50 transition-colors"
              >
                {user?.photoURL ? (
                  <img 
                    src={user.photoURL} 
                    alt={user.displayName || 'User'} 
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-sm font-medium">
                    {(user?.displayName || user?.email || 'U')[0].toUpperCase()}
                  </div>
                )}
                <span className="text-sm text-gray-700 font-medium">
                  {user?.displayName || user?.email?.split('@')[0] || 'User'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {roleMenuItems.map((item, index) => {
                    const Icon = item.icon;
                    // Add divider before Settings
                    const showDivider = item.label === 'Settings';
                    return (
                      <div key={item.path}>
                        {showDivider && <div className="border-t border-gray-200 my-2"></div>}
                        <Link
                          to={item.path}
                          onClick={() => setIsUserMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                            item.label === 'Create Listing'
                              ? 'text-emerald-700 hover:bg-emerald-50 font-medium'
                              : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {item.label}
                        </Link>
                      </div>
                    );
                  })}
                  <div className="border-t border-gray-200 my-2"></div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-emerald-100 bg-white">
            <nav className="px-4 py-3 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors duration-200 ${
                      isActive(item.path)
                        ? 'text-emerald-700 bg-emerald-50'
                        : 'text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
              <div className="border-t border-emerald-100 pt-3 mt-3">
                <div className="flex items-center gap-3 px-4 py-2 mb-2">
                  {user?.photoURL ? (
                    <img 
                      src={user.photoURL} 
                      alt={user.displayName || 'User'} 
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-medium">
                      {(user?.displayName || user?.email || 'U')[0].toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.displayName || 'User'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user?.email}
                    </p>
                  </div>
                </div>
                {roleMenuItems.map((item) => {
                  const Icon = item.icon;
                  const showDivider = item.label === 'Settings';
                  return (
                    <div key={item.path}>
                      {showDivider && <div className="border-t border-emerald-100 my-2"></div>}
                      <Link
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                          item.label === 'Create Listing'
                            ? 'text-emerald-700 bg-emerald-50'
                            : 'text-gray-700 hover:text-emerald-700 hover:bg-emerald-50/50'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        {item.label}
                      </Link>
                    </div>
                  );
                })}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-red-700 hover:bg-red-50/50 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default AppHeader;

